import { Inject, Injectable, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/core';

import { addDays, isAfter } from 'date-fns';
import { ApiService } from 'lib/services/api.service';
import { contains, onAvailable, isDevServer } from 'lib/tools';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import last from 'lodash-es/last';
import remove from 'lodash-es/remove';
import { getProductImageUrl, WebstoreProduct } from 'lib/services/webstore-product/webstore-product.class';
import { RouterService } from 'lib/services/router.service';
import { SwiperSlide } from 'lib/classes/swiper-slide.class';
import { isPlatformBrowser } from '@angular/common';

const scriptName = 'webstore-product.service';

declare let IntelliSuggest: any;

// Keyed per product URL so a TransferState entry can only ever hydrate the page it was
// rendered for (ST-3840).
const productStateKey = (urlComponent: string) => makeStateKey<any>(`product:${urlComponent}`);

@Injectable()
export class WebstoreProductService {
    private apiService: ApiService;
    private environment: any;
    private routerService: RouterService;
    private transferState: TransferState;

    document: Document | undefined;
    constructor(
        @Inject('environment') e: any,
        @Inject(PLATFORM_ID) private platformId: Object,
        a: ApiService,
        r: RouterService,
        t: TransferState,
        @Inject(DOCUMENT) private _doc: any
    ) {
        try {
            this.apiService = a;
            this.environment = e;
            this.routerService = r;
            this.transferState = t;

            // Only inject the document when running in a browser environment
            if (isPlatformBrowser(this.platformId)) {
                this.document = _doc;
            }
        } catch (err) {
            console.error(...new ExxComError(827763, scriptName, err).stamp());
        }
    }

    async init() {
        try {
            // Only initialize browser-specific features when running in browser
            if (isPlatformBrowser(this.platformId)) {
                this.initRecentlyViewedListener();
            }
        } catch (err) {
            console.error(...new ExxComError(398273, scriptName, err).stamp());
        }
    }

    private initRecentlyViewedListener() {
        try {
            this.routerService.onRouteChange('productService', () => {
                if (!new RegExp(this.environment.productUrlRegex).test(this.routerService.url.component)) {
                    return;
                }
                this.maintainRecentlyViewed();
            });
        } catch (err) {
            console.error(...new ExxComError(398182, scriptName, err).stamp());
        }
    }

    private maintainRecentlyViewed() {
        try {
            // Only maintain recently viewed in browser environment
            if (!isPlatformBrowser(this.platformId)) {
                return;
            }

            const urlComponent = this.routerService.url.component;
            const expiration = addDays(new Date(), 3);
            // Get and parse recentlyViewed object from localStorage
            let recentlyViewed: any = [];
            try {
                const stored = localStorage.getItem('recentlyViewed');
                if (stored && stored !== 'undefined' && stored.trim() !== '') {
                    recentlyViewed = JSON.parse(stored);
                }
            } catch (parseError) {
                console.warn('Failed to parse recentlyViewed from localStorage, resetting to empty array');
            }
            // Check if current product already exists
            const found = remove(recentlyViewed, (p: any) => p.urlComponent == urlComponent)[0];
            if (found) {
                // Update existing product expiration
                found.expiresOn = expiration;
                recentlyViewed.push(found);
            } else {
                // Add new WebstoreProduct
                recentlyViewed.push({
                    urlComponent: urlComponent,
                    expiresOn: expiration,
                });
            }
            // Remove old products
            remove(recentlyViewed, (p: any) => isAfter(new Date(), p.expiresOn));
            // Set local storage to the updated array
            localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
        } catch (err) {
            console.error(...new ExxComError(109238, scriptName, err).stamp());
        }
    }

    async getRecentlyViewed(): Promise<SwiperSlide[]> {
        try {
            // Return empty array for SSR - recently viewed is browser-only feature
            if (!isPlatformBrowser(this.platformId)) {
                return [];
            }
            let recentlyViewed = [];
            try {
                const stored = localStorage.getItem('recentlyViewed') || '[]';
                if (stored && stored !== 'undefined') {
                    recentlyViewed = JSON.parse(stored);
                }
            } catch (parseError) {
                console.warn('Failed to parse recentlyViewed, returning empty array');
            }
            const urlComponents = recentlyViewed.map((r: any) => r.urlComponent);
            const products: any = await this.getProductsByUrlComponents(urlComponents);
            const slides = products.map((p: any) => {
                return new SwiperSlide({
                    heading: p.description,
                    imageUrl: p.imageUrl,
                    linkUrl: p.urlComponent,
                });
            });
            return slides;
        } catch (err) {
            console.error(...new ExxComError(409933, scriptName, err).stamp());
        }
    }

    async getRecommendations(nsid: number | string): Promise<SwiperSlide[]> {
        try {
            const slides = [];

            // Return empty array for SSR - recommendations are browser-only feature
            if (!isPlatformBrowser(this.platformId)) {
                return slides;
            }

            // Check if IntelliSuggest is available and document exists
            if (!IntelliSuggest || !this.document) {
                return slides;
            }

            await IntelliSuggest;

            // Create and append temporary element to DOM
            const intellsuggestElem = this.document.createElement('DIV');
            intellsuggestElem.setAttribute('class', 'intellisuggest');
            intellsuggestElem.setAttribute('name', 'Product Recommendations');
            intellsuggestElem.style.visibility = 'hidden';
            this.document.body.appendChild(intellsuggestElem);

            // Initialize IntelliSuggest, which automatically puts the HTML into the element with that class and name
            IntelliSuggest?.init({
                siteId: this.environment.searchspring.siteId,
                context: `Product/${nsid}`,
                seed: [nsid],
            });
            IntelliSuggest?.viewItem({ sku: nsid });
            // Wait for IntelliSuggest to put the elements in the container
            let containerSelector: string = '';
            let intellisuggestSelector: string = '';
            if (this.environment.siteAbbr == 'spc') {
                containerSelector = '.product-details-full-content-related-items';
                intellisuggestSelector = 'product-details-full-content-related-items';
            } else {
                containerSelector = '.intellisuggest-item';
                intellisuggestSelector = 'intellisuggest-item';
            }
            const isAvailable = await onAvailable(containerSelector);
            if (!isAvailable) {
                return slides;
            }
            const elems = intellsuggestElem.getElementsByClassName(intellisuggestSelector);
            if (!get(elems, 'length')) {
                return slides;
            }
            Array.from(elems).forEach((e: HTMLElement) => {
                const anchorElem: HTMLElement = get(e, 'children[0]') as HTMLElement;
                const imgElem: HTMLImageElement = get(anchorElem, 'children[0]') as HTMLImageElement;
                let imgSrc: string;
                let href: string;
                if (this.environment.siteAbbr == 'spc') {
                    href = anchorElem.getAttribute('href');
                    imgSrc = imgElem && imgElem.src;
                } else {
                    href = get(anchorElem, 'children[0].attributes[0].nodeValue');
                    const imgAttr: HTMLElement = get(imgElem, 'children[0]') as HTMLElement;
                    imgSrc = imgAttr && get(imgAttr, 'nodeValue');
                }
                if (!anchorElem || !imgElem || !imgSrc) {
                    return;
                }
                const imgFullFilename: string = last(imgSrc.split('/') || []);
                const imgFilename: string = imgFullFilename && imgFullFilename.split('.')[0];
                if (!imgFilename) {
                    return;
                }
                slides.push(
                    new SwiperSlide({
                        heading: get(e, 'children[1].children[0].innerHTML', ''),
                        imageUrl: getProductImageUrl(imgFilename, this.environment.urls),
                        linkUrl: href,
                    })
                );
            });

            // Safely remove the temporary element
            if (this.document && this.document.body && intellsuggestElem) {
                this.document.body.removeChild(intellsuggestElem);
            }

            return slides;
        } catch (err) {
            console.error(...new ExxComError(491992, scriptName, err).stamp());
            // Return empty array on error to prevent SSR issues
            return [];
        }
    }

    async getProductByMpnComponentConfig(mpn: string) {
        try {
            if (mpn.indexOf('/') == 0) {
                mpn = mpn.slice(1);
            }

            const encodedMpn = encodeURIComponent(mpn);

            const res = await this.apiService.get([
                'products/find',
                `?filters={
            "mpn": "${encodedMpn}"
          }`,
                '&limit=1',
            ]);

            if (!res.success) {
                throw res;
            } else if (isEmpty(res.data)) {
                return null;
            } else {
                return new WebstoreProduct(res.data[0], { environment: this.environment });
            }
        } catch (err) {
            console.error(...new ExxComError(689988, scriptName, err).stamp());
        }
    }

    async getProductByUrlComponent(urlComponent: string, isProductComponent?: boolean) {
        try {
            // Sanitize and validate URL component
            if (urlComponent.indexOf('/') == 0) {
                urlComponent = urlComponent.slice(1);
            }

            // Additional validation to prevent malformed URLs
            if (!urlComponent || urlComponent.trim() === '') {
                console.warn('Invalid URL component provided');
                return null;
            }

            // Angular 16+ TransferState best practices (replaces 50ms delay hack)
            const isServer = !isPlatformBrowser(this.platformId);
            const stateKey = productStateKey(urlComponent);

            let res: any;

            if (isDevServer() || !isProductComponent) {
                // Development or non-product pages: always fetch fresh
                const encodedUrlComponent = encodeURIComponent(`/${urlComponent}`);
                res = await this.apiService.get([
                    'products/find',
                    `?filters={
                        "sites.${this.environment.siteAbbr}.displayInStore": true,
                        "sites.${this.environment.siteAbbr}.urlComponent": ${JSON.stringify(`/${urlComponent}`)}
                    }`,
                    '&limit=1',
                ]);
            } else if (isServer) {
                // Server-side: fetch data and store in TransferState
                const encodedUrlComponent = encodeURIComponent(`/${urlComponent}`);
                res = await this.apiService.get([
                    'products/find',
                    `?filters={
                        "sites.${this.environment.siteAbbr}.displayInStore": true,
                        "sites.${this.environment.siteAbbr}.urlComponent": ${JSON.stringify(`/${urlComponent}`)}
                    }`,
                    '&limit=1',
                ]);
                this.transferState.set(stateKey, res);
            } else {
                // Browser-side: use TransferState if available, otherwise fetch
                if (this.transferState.hasKey(stateKey)) {
                    res = this.transferState.get(stateKey, null);
                    // Clean up the key after using it (Angular recommended pattern)
                    this.transferState.remove(stateKey);
                } else {
                    // No transfer state = client-side navigation, fetch fresh data
                    const encodedUrlComponent = encodeURIComponent(`/${urlComponent}`);
                    res = await this.apiService.get([
                        'products/find',
                        `?filters={
                            "sites.${this.environment.siteAbbr}.displayInStore": true,
                            "sites.${this.environment.siteAbbr}.urlComponent": ${JSON.stringify(`/${urlComponent}`)}
                        }`,
                        '&limit=1',
                    ]);
                }
            }

            if (!res.success) {
                throw res;
            } else if (isEmpty(res.data)) {
                return null;
            } else {
                return new WebstoreProduct(res.data[0], { environment: this.environment });
            }
        } catch (err) {
            console.error(...new ExxComError(680920, scriptName, err).stamp());
            return null;
        }
    }

    async checkIfOffboarded(urlComponent: string): Promise<boolean> {
        try {
            if (urlComponent.indexOf('/') === 0) {
                urlComponent = urlComponent.slice(1);
            }
            const res = await this.apiService.get([
                'products/find',
                `?filters={"sites.${this.environment.siteAbbr}.urlComponent":${JSON.stringify(`/${urlComponent}`)}}`,
                '&fields=_id',
                '&limit=1',
            ]);
            return res.success && !isEmpty(res.data);
        } catch (err) {
            console.error(...new ExxComError(309199, scriptName, err).stamp());
            return false;
        }
    }

    async getProductsByUrlComponents(urlComponents: string[], fields?: string[]) {
        try {
            const siteAbbr = this.environment.siteAbbr;
            const res = await this.apiService.get([
                'products/find',
                `?filters={
          "sites.${siteAbbr}.urlComponent": {
            "$in": ${JSON.stringify(urlComponents)}
          }
        }`,
                `&fields=${JSON.stringify(fields || ['images', `sites.${siteAbbr}.urlComponent`, `sites.${siteAbbr}.description`])}`,
            ]);

            if (!res.success) {
                throw res;
            } else {
                return (res.data || []).map((p: any) => new WebstoreProduct(p, { environment: this.environment }));
            }
        } catch (err) {
            const suppress = ['Unknown Error', 'cannotconnect'];
            if (err && contains(suppress, err.statusText)) {
                return [];
            }
            console.error(...new ExxComError(309198, scriptName, err).stamp());
        }
    }
}
